import React from "react";
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Icon,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Security,
  Storage,
  Business,
  DataUsage,
  People,
  Cookie,
  Public,
  Update,
} from "@mui/icons-material";

interface SubSection {
  subtitle?: string;
  content: string;
}

interface Section {
  title: string;
  icon: React.ReactNode;
  content?: string;
  sections?: SubSection[];
  note?: string;
}

export const Privacy: React.FC = () => {
  const privacyContent: Section[] = [
    {
      title: "Who We Are",
      icon: <Business />,
      content:
        "VenFlows is an ERP system developed and operated by Venustusy IT Solutions, based in Manama, Bahrain. Our goal is to help businesses grow while we handle their operational complexities.",
    },
    {
      title: "Information We Collect",
      icon: <DataUsage />,
      sections: [
        {
          subtitle: "Personal Details",
          content:
            "Name, email, phone number, company name, and user credentials.",
        },
        {
          subtitle: "Usage Data",
          content:
            "Logs, analytics, device/browser info to improve performance.",
        },
        {
          subtitle: "Business Data",
          content:
            "Company inventory, transactions, and configurations added by you.",
        },
      ],
      note: "We do not collect any sensitive personal data unless explicitly provided.",
    },
    {
      title: "How We Use Your Data",
      icon: <Storage />,
      sections: [
        {
          content: "• Deliver and improve our services.",
        },
        {
          content: "• Respond to support requests and inquiries.",
        },
        {
          content: "• Ensure system security and reliability.",
        },
        {
          content:
            "• Send essential system updates and occasional feature announcements.",
        },
      ],
      note: "We do not sell, rent, or trade your information with third parties.",
    },
    {
      title: "Data Storage & Security",
      icon: <Security />,
      content:
        "Your data is securely stored on trusted, encrypted infrastructure. We follow industry-grade security protocols to prevent unauthorized access, including encryption, firewalls, and access controls.",
    },
    {
      title: "Third-Party Services",
      icon: <People />,
      content:
        "We may use trusted third-party services (e.g., for analytics, hosting, or payment gateways), but they are only permitted to process your data on our behalf and under strict confidentiality agreements.",
    },
    {
      title: "Your Rights",
      icon: <Security />,
      sections: [
        {
          content: "• Access the data we hold about you.",
        },
        {
          content: "• Request correction or deletion.",
        },
        {
          content: "• Withdraw consent (where applicable).",
        },
        {
          content: "• Export your data (upon request).",
        },
      ],
      note: "To exercise any of the above, contact us at support@venustusy.com.",
    },
    {
      title: "Cookies",
      icon: <Cookie />,
      content:
        "We use cookies and similar technologies to improve your experience. You can manage your cookie preferences via your browser settings.",
    },
    {
      title: "International Users",
      icon: <Public />,
      content:
        "While our systems are hosted in secure data centers, we serve clients globally. By using our service, you agree to the transfer and storage of data according to this policy.",
    },
    {
      title: "Changes to This Policy",
      icon: <Update />,
      content:
        "We may update this Privacy Policy from time to time. Significant changes will be notified to you via email or in-app.",
    },
  ];

  return (
    <Box sx={{ pb: 8 }}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
          borderBottom: 1,
          borderColor: "divider",
          py: { xs: 8, md: 12 },
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ mb: 4 }}>
            <Link component={RouterLink} to="/" color="inherit">
              Home
            </Link>
            <Typography color="text.primary">Privacy Policy</Typography>
          </Breadcrumbs>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: 800, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            At VenFlows, powered by Venustusy IT Solutions in Bahrain, we take
            your privacy seriously. This Privacy Policy explains how we collect,
            use, and protect your information when you use our ERP platform.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {privacyContent.map((section, index) => (
            <Grid item xs={12} key={section.title}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "16px",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.5rem", md: "1.75rem" },
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Box>

                  {section.content && (
                    <Typography
                      color="text.secondary"
                      sx={{ lineHeight: 1.7, fontSize: "1rem" }}
                    >
                      {section.content}
                    </Typography>
                  )}

                  {section.sections && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mt: section.content ? 2 : 0,
                      }}
                    >
                      {section.sections.map((subsection, idx) => (
                        <Box key={idx}>
                          {subsection.subtitle && (
                            <Typography
                              variant="h6"
                              sx={{
                                mb: 1,
                                color: "primary.main",
                                fontSize: "1.1rem",
                                fontWeight: 600,
                              }}
                            >
                              {subsection.subtitle}
                            </Typography>
                          )}
                          <Typography
                            color="text.secondary"
                            sx={{ lineHeight: 1.7, fontSize: "1rem" }}
                          >
                            {subsection.content}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {section.note && (
                    <Typography
                      sx={{
                        mt: 2,
                        fontSize: "0.9rem",
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      {section.note}
                    </Typography>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            For any questions regarding privacy, contact us at{" "}
            <Link href="mailto:support@venustusy.com" color="primary">
              support@venustusy.com
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
